import * as React from 'react';

import { GameTileProps, GameClickProps } from '../types';
import { Ghost } from '../Banner';
import { BIG_OUTFIT, MEDIUM_BREAKPOINT } from '../constants';
import BannerComponent from '../Banner/BannerComponent';
import { ShouldPreventDefaultForClick } from '../../LaunchStrategiesProvider/utils';
import { CategoryFinancialWebAttributes } from '../../../types/category/categoryTypes';
import { transformImage } from 'src/model/algolia/utils';
import { useWindowSize } from 'src/hooks/useWindowSize';

interface BannerProps {
  topSlot?: GameTileProps;
  onClick?: (
    game: GameClickProps,
    e?: React.MouseEvent<HTMLAnchorElement>
  ) => void;
  onInfoClick?: (
    game: GameClickProps,
    path: string,
    e?: React.MouseEvent<HTMLAnchorElement>
  ) => void;
  ghost?: boolean;
  gameDetailsInfo?: boolean;
  gameInfoTooltip?: string;
  isAnchor?: boolean;
  categoryAttributes?: CategoryFinancialWebAttributes;
}

const prepareBannerImage = (
  image: GameTileProps['bannerImage'],
  width: number,
  fallback: GameTileProps['banner']
): GameTileProps['bannerImage'] =>
  image
    ? {
        ...image,
        source: transformImage(
          image.source.normal,
          width >= MEDIUM_BREAKPOINT.min ? (width - 34) / 2 : width
        ),
      }
    : fallback;

const Banner: React.FC<BannerProps> = ({
  topSlot,
  onClick,
  onInfoClick,
  ghost,
  gameDetailsInfo,
  gameInfoTooltip,
  isAnchor = true,
  categoryAttributes,
}) => {
  const { width } = useWindowSize();
  if (!topSlot && ghost) {
    return <Ghost />;
  }
  if (!topSlot) {
    return null;
  }

  const preventDefaultForGameClick = ShouldPreventDefaultForClick(
    topSlot.integration
  );

  const {
    banner,
    launchUrl,
    gameDetailsPath,
    bannerImage,
    table,
    notIndexed,
  } = topSlot;

  const info = {
    visible: gameDetailsInfo,
    onClick: () => onInfoClick && onInfoClick(topSlot, topSlot.gameDetailsPath),
    gameInfoTooltip,
  };

  const gameLink = {
    visible: !!(isAnchor && launchUrl),
    url: launchUrl,
    gameDetailsPath,
    target: '_blank',
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (preventDefaultForGameClick) e.preventDefault();
      onClick && onClick(topSlot, e);
    },
    notIndexed,
  };
  const sashLayer = topSlot.sash && {
    sash: topSlot.sash.title,
    background: topSlot.sash.background,
    outfit: BIG_OUTFIT,
  };

  return (
    <BannerComponent
      ghost={ghost}
      asset={prepareBannerImage(bannerImage, width, banner)}
      info={info}
      gameLink={gameLink}
      sashLayer={sashLayer}
      tableLayer={table}
      jackpotLayer={topSlot.jackpot}
      gameBannerTitle={topSlot.title}
      categoryAttributes={categoryAttributes}
    />
  );
};

export default Banner;
